'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const beforeItems = [
  { text: 'Waiting 3–5 days for proposals', icon: '⏳' },
  { text: 'Posting jobs on 3 different platforms', icon: '📋' },
  { text: 'Comparing 12 different proposals', icon: '😩' },
  { text: 'Endless email threads before starting', icon: '📧' },
  { text: 'No visibility into actual work happening', icon: '🔒' },
  { text: 'Chasing payments for 30+ days', icon: '💸' },
];

const afterItems = [
  { text: 'Matched with an expert in 47 seconds', icon: '⚡' },
  { text: 'One platform. Everything included.', icon: '✅' },
  { text: 'Zero proposals — instant match', icon: '🎯' },
  { text: 'Live session starts immediately', icon: '🚀' },
  { text: 'Full screen share — total transparency', icon: '🖥️' },
  { text: 'Instant payout upon approval', icon: '💰' },
];

export default function BeforeAfterSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-foreground relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14 reveal">
          <p className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3">The Reality Check</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight">
            The old way is broken.
            <br />
            <span className="text-primary">We fixed it.</span>
          </h2>
        </div>

        {/* Before / After Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* BEFORE */}
          <div className="before-card rounded-3xl p-8 reveal-left">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-sm">😤</span>
              </div>
              <h3 className="text-lg font-black text-foreground uppercase tracking-wider">Before Summons</h3>
            </div>
            <div className="space-y-3">
              {beforeItems?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 bg-white/60 rounded-xl">
                  <span className="text-base flex-shrink-0">{item?.icon}</span>
                  <span className="text-sm text-gray-600 line-through decoration-red-400/60">{item?.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AFTER */}
          <div className="after-card rounded-3xl p-8 reveal-right" style={{ transitionDelay: '0.15s' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-sm">🧞</span>
              </div>
              <h3 className="text-lg font-black text-foreground uppercase tracking-wider">With Summons</h3>
            </div>
            <div className="space-y-3">
              {afterItems?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 bg-white/70 rounded-xl">
                  <span className="text-base flex-shrink-0">{item?.icon}</span>
                  <span className="text-sm font-semibold text-foreground">{item?.text}</span>
                  <div className="ml-auto w-4 h-4 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckIcon" size={10} className="text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Central stat */}
        <div className="mt-10 text-center reveal" style={{ transitionDelay: '0.2s' }}>
          <div className="inline-flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl px-8 py-5">
            <div className="text-center">
              <p className="text-4xl font-black text-primary">47s</p>
              <p className="text-white/50 text-xs font-medium mt-1">Avg. match time</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-4xl font-black text-accent">98%</p>
              <p className="text-white/50 text-xs font-medium mt-1">Session satisfaction</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-4xl font-black text-highlight">$0</p>
              <p className="text-white/50 text-xs font-medium mt-1">Upfront risk</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}